import React, { Fragment } from "react";
import { makePhotoSource } from "utils/constants";
import { QuotedMessage } from "./quotedMessage";

const SKELETON_ROW_WIDTHS = [65, 45, 72, 58, 38];

export const ChatMessageList = ({
  scrollContainerRef,
  initialScrollPendingRef,
  scrollToBottom,
  isFetchingInitial,
  messages,
  topSentinelRef,
  isJumpingToMessage,
  t,
  getTopHistoryStatus,
  bottomSentinelRef,
  messagesEndRef,
  isPolling,
  currentLoginUserId,
  doctorType,
  user,
  getUserName,
  handleJumpToMessage,
  handleContextMenu,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
  onStartEditMessage,
  onRequestDeleteMessage,
  getDate,
  audioRefs,
  audioSpeed,
  onAudioSpeedChange,
  setPreviewImage,
}) => {
  const renderMessageSkeleton = () => (
      <div style={{ paddingTop: "8px" }}>
        <div style={{ marginBottom: "8px", color: "#666", fontSize: "12px" }}>
          {t("resources.chat.initialLoadingMessages")}
        </div>
        {SKELETON_ROW_WIDTHS.map((width, index) => (
            <div
                key={`chat-skeleton-${index}`}
                style={{
                  width: `${width}%`,
                  minWidth: "180px",
                  height: "52px",
                  borderRadius: "10px",
                  marginBottom: "10px",
                  background: "linear-gradient(90deg, #f2f2f2 25%, #e8e8e8 37%, #f2f2f2 63%)",
                  backgroundSize: "400% 100%",
                  animation: "pulse 1.4s ease infinite",
                }}
            />
        ))}
      </div>
  );

  const bubbleActions = (msg) => {
    const isOwn = msg?.doctor?.id === currentLoginUserId && doctorType;
    return (
        <div style={{ display: "flex", alignItems: "center", width: "fit-content", gap: "4px" }}>
          {isOwn && msg.type === "text" && (
              <img
                  src={`${process.env.PUBLIC_URL}/edit.svg`}
                  alt="Edit"
                  style={{ cursor: "pointer", width: 16, height: 16, marginLeft: 8 }}
                  onClick={() => onStartEditMessage(msg)}
              />
          )}
          {isOwn && (
              <img
                  src={`${process.env.PUBLIC_URL}/red-trash-icon.svg`}
                  alt="Delete"
                  style={{ cursor: "pointer", width: 16, height: 16, marginLeft: 8 }}
                  onClick={() => onRequestDeleteMessage(msg.id)}
              />
          )}
        </div>
    );
  };

  const msgWrapperProps = (msg) => ({
    style: { marginBottom: "10px", position: "relative" },
    "data-message-id": msg?.id,
    onContextMenu: (e) => handleContextMenu(e, msg),
    onTouchStart: (e) => onTouchStart(e, msg),
    onTouchMove,
    onTouchEnd,
    onTouchCancel,
  });

  return (
      <div
          ref={scrollContainerRef}
          onLoadCapture={() => {
            if (!initialScrollPendingRef.current) return;
            scrollToBottom({ behavior: "auto" });
          }}
          onLoadedMetadataCapture={() => {
            if (!initialScrollPendingRef.current) return;
            scrollToBottom({ behavior: "auto" });
          }}
          style={{ border: "1px solid #ccc", padding: "10px", overflowY: "scroll" }}
      >
        {isFetchingInitial ? (
            renderMessageSkeleton()
        ) : messages.length > 0 ? (
            <>
              <div ref={topSentinelRef} style={{ height: 1 }} />
              {isJumpingToMessage && (
                  <div style={{ textAlign: "center", fontSize: "12px", marginBottom: "10px", color: "#666" }}>
                    {t("resources.chat.jumpToMessageLoading")}
                  </div>
              )}
              {getTopHistoryStatus() && (
                  <div style={{ textAlign: "center", fontSize: "12px", marginBottom: "10px", color: "#666" }}>
                    {getTopHistoryStatus()}
                  </div>
              )}
              {messages.map((msg, index) => {
                const isOwn = msg?.doctor?.id === currentLoginUserId && doctorType;
                const bubbleBg = msg.sender === "doctor" ? "#d0e7ff" : "#f1f1f1";

                return (
                    <Fragment key={msg.id}>

                      {msg.type === "text" && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  maxWidth: "100%",
                                  wordBreak: "break-word",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName} (doctor)`
                                    : user?.name + " (client)"}:
                              </strong>

                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />

                              <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    whiteSpace: "pre-wrap",
                                    overflowWrap: "break-word",
                                  }}
                              >
                                {msg.text}
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong>{getDate(msg.createdAt)}</strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}

                      {msg.type === "photo" && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  width: "min-content",
                                  height: "fit-content",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName} (doctor)`
                                    : user?.name + " (client)"}:{" "}
                              </strong>
                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />
                              <img
                                  style={{ maxWidth: "300px", maxHeight: "300px", height: "auto" }}
                                  src={makePhotoSource(msg.text)}
                                  onClick={() => setPreviewImage(makePhotoSource(msg.text))}
                                  alt=""
                              />
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong style={{ textAlign: "right", display: "inline-block", width: "100%" }}>
                                  {getDate(msg.createdAt)}
                                </strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}

                      {msg.type === "document" && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  maxWidth: "100%",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName} (doctor)`
                                    : user?.name + " (client)"}:{" "}
                              </strong>
                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />
                              <a
                                  href={makePhotoSource(msg.text)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                              >
                                <img
                                    src={process.env.PUBLIC_URL + "/file-view.png"}
                                    alt="File"
                                    style={{ width: "20px", height: "20px" }}
                                />
                                Download File
                              </a>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong style={{ textAlign: "right", display: "inline-block", width: "100%" }}>
                                  {getDate(msg.createdAt)}
                                </strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}

                      {msg.type === "video_note" && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  maxWidth: "100%",
                                  height: "400px",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName}`
                                    : user.name}:{" "}
                              </strong>
                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />
                              <div style={{ height: "364px", marginBottom: "8px" }}>
                                <video
                                    controls
                                    style={{ display: "flex", width: "100%", maxWidth: "300px", height: "100%", borderRadius: "8px" }}
                                >
                                  <source src={makePhotoSource(msg.text)} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong style={{ textAlign: "right", display: "inline-block", width: "100%" }}>
                                  {getDate(msg.createdAt)}
                                </strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}

                      {msg.type === "video" && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  maxWidth: "100%",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName}`
                                    : user.name}:{" "}
                              </strong>
                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />
                              <div style={{ height: "364px", marginBottom: "8px" }}>
                                <video
                                    controls
                                    style={{ display: "flex", width: "100%", maxWidth: "300px", height: "100%", borderRadius: "8px" }}
                                >
                                  <source src={makePhotoSource(msg.text)} type="video/mp4" />
                                  Your browser does not support the video tag.
                                </video>
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong style={{ textAlign: "right", display: "inline-block", width: "100%" }}>
                                  {getDate(msg.createdAt)}
                                </strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}

                      {(msg.type === "voice" || msg.type === "audio") && (
                          <div {...msgWrapperProps(msg)}>
                            <div
                                style={{
                                  display: "inline-block",
                                  backgroundColor: bubbleBg,
                                  padding: "8px",
                                  borderRadius: "8px",
                                  maxWidth: "100%",
                                }}
                            >
                              <strong>
                                {msg.sender === "doctor"
                                    ? `${msg?.doctor?.firstName} ${msg?.doctor?.lastName}`
                                    : user?.name}:{" "}
                              </strong>
                              <QuotedMessage
                                  parentMessage={msg.parentMessage ?? msg.replyTo}
                                  messages={messages}
                                  getUserName={getUserName}
                                  onJumpToMessage={handleJumpToMessage}
                                  labels={{
                                    deleted: t("resources.chat.quotedMessageDeleted"),
                                    notFound: t("resources.chat.quotedMessageNotFound"),
                                  }}
                              />
                              <audio
                                  ref={(el) => {
                                    if (el) {
                                      audioRefs.current[index] = el;
                                      el.playbackRate = audioSpeed;
                                    }
                                  }}
                                  controls
                                  onLoadedMetadata={(e) => { e.currentTarget.playbackRate = audioSpeed; }}
                                  style={{ display: "flex", maxWidth: "400px", alignItems: "center" }}
                              >
                                <source
                                    src={makePhotoSource(msg.text)}
                                    type={makePhotoSource(msg.text)?.includes("oga") ? "audio/ogg" : "audio/mpeg"}
                                />
                                Your browser does not support the audio element.
                              </audio>
                              <div style={{ display: "flex", alignItems: "center", width: "100%", gap: "4px", padding: "4px" }}>
                                {[0.5, 1, 1.5, 2].map((speed) => (
                                    <button
                                        key={speed}
                                        style={{
                                          fontSize: 12,
                                          padding: speed === 0.5 ? "4px" : "4px 10px",
                                          borderRadius: 4,
                                          border: "0",
                                          background: audioSpeed === speed ? "#1976d2" : "#fff",
                                          color: audioSpeed === speed ? "#fff" : "#555",
                                          cursor: "pointer",
                                          fontFamily: "inherit",
                                          whiteSpace: "nowrap",
                                        }}
                                        onClick={() => onAudioSpeedChange(speed)}
                                    >
                                      {speed}x
                                    </button>
                                ))}
                              </div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                                <strong style={{ textAlign: "right", display: "inline-block", width: "100%" }}>
                                  {getDate(msg.createdAt)}
                                </strong>
                                {isOwn && bubbleActions(msg)}
                              </div>
                            </div>
                          </div>
                      )}
                    </Fragment>
                );
              })}
              <div ref={bottomSentinelRef} style={{ height: 1 }} />
              <div ref={messagesEndRef} />
            </>
        ) : (
            <>
              {isPolling && (
                  <div style={{ textAlign: "center", fontSize: "12px", marginBottom: "8px", color: "#666" }}>
                    {t("resources.chat.initialLoadingMessages")}
                  </div>
              )}
              <p>{t("resources.chat.noMessages")}</p>
            </>
        )}
      </div>
  );
};
